import React, { useCallback, useEffect, useRef } from 'react';
import useAuth from 'hooks/useAuth';
import {
  appendDedicatedEnv,
  CHECKOUT_STEPS,
  EMPTY_DEDICATED_PATCH,
  hasOwnerBoundDedicated,
  listActiveSubscriptions,
  selectionFromAllSubscriptions
} from 'lib/billing';
import { useBillingStore, useCheckoutStore, useCouponValidationSync, useInvoicePreviewSync } from './store';
import type { CheckoutCompleteHandler } from './store/types';

type Props = {
  children: React.ReactNode;
  initialStep?: string;
  onComplete?: CheckoutCompleteHandler;
};

/**
 * Bootstrap component for the checkout flow. Owns the side effects that wire
 * the global stores (`useCheckoutStore`, `useBillingStore`) into a usable
 * checkout session:
 *
 *   1. Pins the initial step in the store on mount.
 *   2. Publishes the parent-supplied `onComplete` callback into the store so
 *      step components (e.g. PaymentStep) can fire it without prop drilling.
 *   3. Hydrates form fields (email / firstName / lastName) from the auth user
 *      once on mount.
 *   4. Clears owner-bound dedicated selections on sign-out so a different
 *      user signing into the same tab cannot inherit them.
 *   5. Re-derives the selection from the user's active subscriptions whenever
 *      the subscriptions/catalog set materially changes (post-login,
 *      post-purchase, etc.).
 *
 * No React Context is published — consumers read state via the store hooks
 * (`useCheckoutState` / `useCheckoutActions` and the targeted selectors).
 */
export function CheckoutProvider({ children, initialStep = CHECKOUT_STEPS.configure, onComplete }: Props) {
  const { isAuthenticated, isLoading: authLoading, user, accessToken } = useAuth() as any;
  const authCheckout = Boolean(isAuthenticated && accessToken && !authLoading);

  const selection = useCheckoutStore((s) => s.selection);
  const setSelection = useCheckoutStore((s) => s.setSelection);
  const pendingNewEnvKind = useCheckoutStore((s) => s.pendingNewEnvKind);
  const pendingQuickPatch = useCheckoutStore((s) => s.pendingQuickPatch);
  const setPendingNewEnvKind = useCheckoutStore((s) => s.setPendingNewEnvKind);
  const setForm = useCheckoutStore((s) => s.setForm);
  const setStep = useCheckoutStore((s) => s.setStep);
  const setOnComplete = useCheckoutStore((s) => s.setOnComplete);

  const subscriptions = useBillingStore((s) => s.subscriptions);
  const billingOverview = useBillingStore((s) => s.overview);
  const catalog = useBillingStore((s) => s.catalog);
  const subscriptionsLoading = useBillingStore((s) => s.loading);
  const fetchBilling = useBillingStore((s) => s.fetch);
  const catalogLoading = useBillingStore((s) => s.catalogLoading);

  const hydratedSubsKeyRef = useRef<string | null>(null);
  const initialStepAppliedRef = useRef(false);
  const drawerOpen = useCheckoutStore((s) => s.drawerOpen);
  const prevDrawerOpenRef = useRef(drawerOpen);

  useInvoicePreviewSync();
  useCouponValidationSync();

  useEffect(() => {
    if (initialStepAppliedRef.current) return;
    initialStepAppliedRef.current = true;
    // The drawer body mounts through a MUI Portal, one commit AFTER the
    // drawer itself — so when the drawer mounts already-open (quick-subscribe
    // staged the cart on the welcome tour, then navigated here), the drawer's
    // open-effect has ALREADY applied the requested entry step. Applying the
    // default over it would bounce a Payment entry back to Configure.
    if (useCheckoutStore.getState().requestedStep) return;
    setStep(initialStep as any);
  }, [initialStep, setStep]);

  useEffect(() => {
    setOnComplete(onComplete ?? null);
    return () => setOnComplete(null);
  }, [onComplete, setOnComplete]);

  useEffect(() => {
    if (!user?.email) return;
    const parts = String(user.name || '')
      .trim()
      .split(/\s+/);
    setForm({
      email: user.email,
      firstName: parts[0] || '',
      lastName: parts.length > 1 ? parts.slice(1).join(' ') : ''
    });
  }, [user?.email, user?.name, setForm]);

  useEffect(() => {
    if (authCheckout) return;
    if (!hasOwnerBoundDedicated(selection)) return;
    setSelection({ ...EMPTY_DEDICATED_PATCH } as any);
  }, [authCheckout, selection, setSelection]);

  useEffect(() => {
    // Every quote is planned against the subscription list, so a stale one
    // (a second tab, a purchase made elsewhere, a webhook not yet applied)
    // plans changes against a subscription that has already moved: seats read
    // as 9 when Stripe says 10, and "set it to 10" becomes a no-op the server
    // has to recognise. Refresh on each open — one request, and the drawer
    // starts from what the customer actually owns.
    if (!drawerOpen || !authCheckout || !user?.tcId) return;
    fetchBilling(user.tcId, { force: true });
  }, [drawerOpen, authCheckout, user?.tcId, fetchBilling]);

  // A first-time buyer lands on the smallest setup that can actually copy: a
  // source and a destination. Seeding lives here rather than in
  // DEFAULT_SELECTION because that object is also the base for carts this must
  // never touch — a Dedicated-Environment-only purchase resets to it (above),
  // and so do the MCP-standalone, EA Hosting and Signal Manager flows, where
  // two silent copier accounts would add $20/mo the buyer never asked for.
  const seedCopierPairIfEmpty = useCallback(() => {
    const sel: any = useCheckoutStore.getState().selection;
    if (sel.signalManagerSelected || sel.mcpStandalone || sel.eaHostingStandalone) return;
    if ((sel.standardCount || 0) + (sel.premiumCount || 0) + (sel.futuresCount || 0) > 0) return;
    if (Array.isArray(sel.dedicated) && sel.dedicated.length > 0) return;
    setSelection({ standardCount: 2 } as any);
  }, [setSelection]);

  useEffect(() => {
    // A drawer session just ended. Flows like DirectBuyMcpModal reset the
    // selection before opening (add-on-only cart); without dropping the
    // hydration key here the guard below would treat the wiped cart as current
    // and every later open this session would show a phantom-empty plan.
    if (prevDrawerOpenRef.current && !drawerOpen) {
      hydratedSubsKeyRef.current = null;
    }
    prevDrawerOpenRef.current = drawerOpen;

    if (!authCheckout || subscriptionsLoading || catalogLoading) return;
    // Include trialing (match selectionFromAllSubscriptions) so trial-only subs seed on open.
    const activeSubs = listActiveSubscriptions(subscriptions as any[]);
    if (!activeSubs.length) {
      // No active subs: reset so the next open starts empty — unless a
      // "Purchase Additional Env" flow is pending, then seed that one new env.
      if (pendingNewEnvKind) {
        useCheckoutStore.getState().resetSelection();
        setSelection(appendDedicatedEnv(null, pendingNewEnvKind));
        setPendingNewEnvKind(null);
      } else {
        if (hydratedSubsKeyRef.current !== null) useCheckoutStore.getState().resetSelection();
        if (pendingQuickPatch) {
          // Quick-subscribe cart for a user with no active subs: apply onto the
          // fresh default selection. Goes BEFORE the seed so the buyer's explicit
          // choice is never topped up with a silent copier pair.
          setSelection(pendingQuickPatch as any);
          useCheckoutStore.getState().setPendingQuickPatch(null);
        }
        seedCopierPairIfEmpty();
      }
      hydratedSubsKeyRef.current = null;
      return;
    }
    const hydrationKey = activeSubs
      .map((s: any) => {
        const lines = (s.lineItems || []).map((li: any) => `${li.priceId}:${li.quantity}`).join(',');
        return `${s.id}:${s.quantity}:${lines}:${s.status}`;
      })
      .sort()
      .join('|');
    // A pending env-add or quick cart must still apply when the hydration key
    // is unchanged (reopening the drawer without a subscription change).
    if (hydratedSubsKeyRef.current === hydrationKey && !pendingNewEnvKind && !pendingQuickPatch) return;
    const next = selectionFromAllSubscriptions(subscriptions, billingOverview, catalog as any);
    if (next) {
      // Merge pendings onto the hydrated subs (one patch, no render race).
      // The quick patch rides ON TOP of hydration: for an add-on-only
      // subscriber the hydrated selection carries their analyzer/DE lines,
      // and the quick patch adds the copier counts they just chose.
      setSelection({
        ...next,
        ...(pendingNewEnvKind ? appendDedicatedEnv(next, pendingNewEnvKind) : null),
        ...(pendingQuickPatch || null)
      });
      hydratedSubsKeyRef.current = hydrationKey;
      if (pendingNewEnvKind) setPendingNewEnvKind(null);
      if (pendingQuickPatch) useCheckoutStore.getState().setPendingQuickPatch(null);
    } else if (pendingQuickPatch) {
      // Hydration produced nothing while the user still HAS active subs — e.g.
      // every one of them is a Signal Manager room, which
      // selectionFromAllSubscriptions filters out. Dropping the staged cart
      // here opened the drawer empty (or on the wrong step, since standalone
      // flags from a previous selection survive), so apply it over a clean
      // default and spend the one-shot patch.
      useCheckoutStore.getState().resetSelection();
      setSelection(pendingQuickPatch as any);
      useCheckoutStore.getState().setPendingQuickPatch(null);
      hydratedSubsKeyRef.current = hydrationKey;
    }
  }, [
    authCheckout,
    subscriptions,
    billingOverview,
    catalog,
    subscriptionsLoading,
    catalogLoading,
    setSelection,
    drawerOpen,
    pendingNewEnvKind,
    pendingQuickPatch,
    setPendingNewEnvKind,
    seedCopierPairIfEmpty
  ]);

  return <>{children}</>;
}
